import type { SignUpFormValues } from '../../schemas';
import type { Address } from '@paid-ui/schemas/zod/address';

import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import { FormControl, FormField, FormHelper, FormItem } from '@paid-ui/components/form';
import { Input } from '@paid-ui/components/input';
import { MobileInput } from '@paid-ui/components/mobile-input';
import { PasswordInput } from '@paid-ui/components/password-input';

import { TermsCheckbox } from '../../components/terms-checkbox';
import { signUpManager } from '../../models';
import { SignUpStep } from '../../models/enums';
import { useCompleteSignUp } from '../../mutations/complete-sign-up';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

export const StepUserDetails: React.FC<Props> = ({ disabled, goToStep }) => {
  const form = useFormContext();
  const { currentStep, invitedSignUp, userGroupId } = useSnapshot(signUpManager);
  const { mutate: signUp, isLoading: isSubmitting } = useCompleteSignUp();
  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');
  const mobile = form.watch('mobile');
  const password = form.watch('password');
  const agreedOnTerms = form.watch('agreedOnTerms');

  const isValid =
    Boolean(agreedOnTerms) &&
    Boolean(firstName && lastName && mobile && password) &&
    !form.getFieldState('firstName').invalid &&
    !form.getFieldState('lastName').invalid &&
    !form.getFieldState('mobile').invalid &&
    !form.getFieldState('password').invalid;

  const handleNext = () => {
    if (invitedSignUp) {
      signUp(form.getValues() as SignUpFormValues);
    } else {
      goToStep?.(SignUpStep.ONE_TIME_CODE);
    }
  };

  if (currentStep !== SignUpStep.USER_DETAILS) {
    return null;
  }

  return (
    <div className="space-y-9">
      <div className="space-y-3.5">
        <FormField
          name="firstName"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder="First name" disabled={disabled || isSubmitting} />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
        <FormField
          name="lastName"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder="Last name" disabled={disabled || isSubmitting} />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
        <FormField
          name="mobile"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <MobileInput
                  {...field}
                  autoComplete="off"
                  placeholder="Mobile number"
                  disabled={disabled || isSubmitting}
                />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
        <FormField
          name="password"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PasswordInput
                  {...field}
                  autoComplete="off"
                  placeholder="Password"
                  disabled={disabled || isSubmitting}
                />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
        <TermsCheckbox hidden={!userGroupId} />
      </div>
      <Button
        variant="solid"
        className="h-[44px]"
        disabled={disabled || !isValid}
        loading={isSubmitting}
        onClick={handleNext}
      >
        {isSubmitting ? 'Submitting...' : 'Continue'}
      </Button>
    </div>
  );
};
